/**
 * RTK Query tags for the audit module, in one place.
 *
 * A mutation says which *event* it causes (a request item changed, a finding changed, the audit
 * closed) and gets back every tag whose data that event touches, so adding a query that shows
 * some of that data means editing one bundle, not hunting through every mutation.
 * `auditTags.test.ts` pins the full mutation x query matrix.
 *
 * Tags only refresh this browser's cache; what another user did shows up on the next visit
 * (the cross-user queries use `keepUnusedDataFor: 0`, see auditApi.ts).
 */

/**
 * Cross-audit finding lists: the Finding Register and its stat cards, and the finding/CAP My
 * Actions lists. Neither `{ AuditFindings, <auditId> }` nor bare `'Audits'` matches it.
 */
export const FINDING_REGISTER_TAG = { type: 'AuditFindings' as const, id: 'REGISTER' };

type AuditScopedTagType =
  'Audits' | 'AuditFindings' | 'AuditRegisterLog' | 'AuditPreliminaryReport' | 'AuditFinalReport';

export type AuditTag =
  | 'Audits'
  | 'AuditRegistry'
  | 'AuditReports'
  | 'AuditRequestItems'
  | { type: AuditScopedTagType; id: string };

type AuditArg = { auditId: string };

const scoped = (type: AuditScopedTagType, auditId: string): AuditTag => ({ type, id: auditId });

/** Turns a bundle into the `invalidatesTags` callback of a mutation whose argument has `auditId`. */
export const invalidatesFor =
  (bundle: (auditId: string) => AuditTag[]) =>
  (_result: unknown, _error: unknown, { auditId }: AuditArg): AuditTag[] =>
    bundle(auditId);

/** A request item was created, reassigned, or had evidence supplied/reviewed. Writes a log entry. */
export const requestItemsChanged = (auditId: string): AuditTag[] => [
  'AuditRequestItems',
  scoped('AuditRegisterLog', auditId),
];

/**
 * A request item's status moved to or from accepted: the registry row and the audit detail show
 * accepted/total, and a KPI counts the outstanding ones.
 */
export const requestItemCountsChanged = (auditId: string): AuditTag[] => [
  ...requestItemsChanged(auditId),
  'AuditRegistry',
  scoped('Audits', auditId),
];

/**
 * A finding, its acknowledgment, closure, CAP or a CAP action changed. Shows up in the Finding
 * Details modal, the registry / reports lists and KPIs (bare 'Audits'), that audit's findings
 * table, the Finding Register and the My Actions lists, and the Audit Log.
 */
export const findingChanged = (auditId: string): AuditTag[] => [
  'Audits',
  scoped('AuditFindings', auditId),
  FINDING_REGISTER_TAG,
  scoped('AuditRegisterLog', auditId),
];

/**
 * The final report was recorded and the audit closed. A closed audit leaves the My Actions
 * request-item inbox and every open-audit finding list.
 */
export const auditClosed = (auditId: string): AuditTag[] => [
  'Audits',
  'AuditRequestItems',
  FINDING_REGISTER_TAG,
  scoped('AuditFinalReport', auditId),
  scoped('AuditRegisterLog', auditId),
];

/** The preliminary report was recorded: the audit may start, and its findings are created. */
export const preliminaryReportCreated = (auditId: string): AuditTag[] => [
  'Audits',
  scoped('AuditPreliminaryReport', auditId),
  scoped('AuditFindings', auditId),
  FINDING_REGISTER_TAG,
  scoped('AuditRegisterLog', auditId),
];

/** The preliminary report's own fields (dates, conclusion, file) were edited. */
export const preliminaryReportEdited = (auditId: string): AuditTag[] => [
  scoped('AuditPreliminaryReport', auditId),
  'AuditReports',
  scoped('AuditRegisterLog', auditId),
];

/** Findings were raised in bulk. */
export const findingsRaised = (auditId: string): AuditTag[] => [
  scoped('Audits', auditId),
  'AuditRegistry',
  scoped('AuditFindings', auditId),
  'AuditReports',
  scoped('AuditRegisterLog', auditId),
  FINDING_REGISTER_TAG,
];

/** One finding's own fields were edited. */
export const findingEdited = (auditId: string): AuditTag[] => [
  scoped('AuditFindings', auditId),
  scoped('Audits', auditId),
  scoped('AuditRegisterLog', auditId),
  FINDING_REGISTER_TAG,
];
